import { FC, useRef, KeyboardEvent, ClipboardEvent, ChangeEvent } from 'react';
import { Box, TextField } from '@mui/material';

interface OtpCodeInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
}

const CODE_LENGTH = 6;

const OtpCodeInput: FC<OtpCodeInputProps> = ({
  value,
  onChange,
  disabled = false,
  error = false,
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const digits = Array.from({ length: CODE_LENGTH }, (_, i) => value[i] ?? '');

  const focusAt = (index: number): void => {
    const clamped = Math.max(0, Math.min(CODE_LENGTH - 1, index));
    inputRefs.current[clamped]?.focus();
  };

  const handleChange = (
    index: number,
    e: ChangeEvent<HTMLInputElement>
  ): void => {
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) {
      return;
    }
    const digit = raw[raw.length - 1];
    const newDigits = [...digits];
    newDigits[index] = digit;
    onChange(newDigits.join(''));
    if (index < CODE_LENGTH - 1) {
      focusAt(index + 1);
    }
  };

  const handleKeyDown = (
    index: number,
    e: KeyboardEvent<HTMLInputElement>
  ): void => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const newDigits = [...digits];
      if (newDigits[index]) {
        newDigits[index] = '';
        onChange(newDigits.join(''));
      } else if (index > 0) {
        newDigits[index - 1] = '';
        onChange(newDigits.join(''));
        focusAt(index - 1);
      }
    } else if (e.key === 'ArrowLeft') {
      focusAt(index - 1);
    } else if (e.key === 'ArrowRight') {
      focusAt(index + 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>): void => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, CODE_LENGTH);
    if (pasted) {
      onChange(pasted.padEnd(CODE_LENGTH, '').slice(0, CODE_LENGTH));
      focusAt(Math.min(pasted.length, CODE_LENGTH - 1));
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
      {digits.map((digit, i) => (
        <TextField
          key={i}
          inputRef={(el) => {
            inputRefs.current[i] = el;
          }}
          value={digit}
          onChange={(e) => handleChange(i, e as ChangeEvent<HTMLInputElement>)}
          onKeyDown={(e) =>
            handleKeyDown(i, e as KeyboardEvent<HTMLInputElement>)
          }
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          disabled={disabled}
          error={error}
          inputProps={{
            maxLength: 1,
            inputMode: 'numeric',
            pattern: '[0-9]*',
            'aria-label': `Verification code digit ${i + 1} of ${CODE_LENGTH}`,
            style: {
              textAlign: 'center',
              fontSize: '1.5rem',
              padding: '8px 0',
            },
          }}
          sx={{ width: 48 }}
        />
      ))}
    </Box>
  );
};

export default OtpCodeInput;
